import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Input,
  Select,
  Card,
  DataTable,
  Modal,
} from "../../components/ui";
import { ConfirmModal } from "../../components/ui/Modal";

// Tipi Azione disponibili
const ACTION_TYPES = [
  { value: "100000000", label: "Aggiorna Intervento" },
  { value: "100000001", label: "Crea Scadenza" },
  { value: "100000002", label: "Upload Documento" },
  { value: "100000003", label: "Completa (Tutte)" },
  { value: "100000004", label: "Elabora Bolletta" },
];

const getActionTypeLabel = (value) => {
  const found = ACTION_TYPES.find((a) => a.value === String(value));
  return found ? found.label : "-";
};

// Template JSON config di default
const DEFAULT_CONFIG = {
  extractors: {
    data_intervento: {
      pattern: "(\\d{1,2}[\\/\\-]\\d{1,2}[\\/\\-]\\d{2,4})",
      format: "DD/MM/YYYY",
      field: "phc_dataesecuzione",
    },
    rsa: {
      pattern: "RSA\\s+([A-Za-z\\s]+?)(?:\\s*[-,]|$)",
      lookup_table: "phc_rsas",
      lookup_field: "phc_name",
    },
  },
  actions: {
    update_intervento: {
      enabled: true,
      update_fields: {
        phc_stato: 100000002,
      },
    },
    upload_attachments: {
      enabled: true,
      folder_pattern: "Rapportini/{year}/{rsa_name}",
      allowed_extensions: [".pdf", ".jpg", ".png", ".xlsx", ".docx"],
    },
  },
};

function EmailMappingPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [mappings, setMappings] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    nome: "",
    fornitoreId: "",
    emailPattern: "",
    subjectPattern: "",
    datePattern: "",
    rsaPattern: "",
    interventoPattern: "",
    actionType: "100000003",
    mappingConfig: JSON.stringify(DEFAULT_CONFIG, null, 2),
    attivo: true,
    priorita: 10,
    pdfRouter: false,
  });

  // Test form state
  const [testData, setTestData] = useState({
    emailFrom: "",
    subject: "",
    body: "",
  });
  const [testResults, setTestResults] = useState(null);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mappingsData, fornitoriData] = await Promise.all([
        apiRequest("/email-mapping"),
        apiRequest("/fornitori"),
      ]);
      setMappings(mappingsData.value || []);
      setFornitori(fornitoriData.value || []);
    } catch (error) {
      console.error("Error loading data:", error);
      showToast("Errore nel caricamento dei dati", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      nome: "",
      fornitoreId: "",
      emailPattern: "",
      subjectPattern: "",
      datePattern: "",
      rsaPattern: "",
      interventoPattern: "",
      actionType: "100000003",
      mappingConfig: JSON.stringify(DEFAULT_CONFIG, null, 2),
      attivo: true,
      priorita: 10,
      pdfRouter: false,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nome: item.nome || "",
      fornitoreId: item.fornitoreId || "",
      emailPattern: item.emailPattern || "",
      subjectPattern: item.subjectPattern || "",
      datePattern: item.datePattern || "",
      rsaPattern: item.rsaPattern || "",
      interventoPattern: item.interventoPattern || "",
      actionType: String(item.actionType || "100000003"),
      mappingConfig: item.mappingConfig
        ? JSON.stringify(item.mappingConfig, null, 2)
        : JSON.stringify(DEFAULT_CONFIG, null, 2),
      attivo: item.attivo !== undefined ? item.attivo : true,
      priorita: item.priorita || 10,
      pdfRouter: item.mappingConfig?.pdf_router === true,
    });
    setModalOpen(true);
  };

  const handleOpenTest = (item) => {
    setEditingItem(item);
    setTestData({
      emailFrom: "",
      subject: "",
      body: "",
    });
    setTestResults(null);
    setTestModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTestChange = (e) => {
    const { name, value } = e.target;
    setTestData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      showToast("Il nome è obbligatorio", "error");
      return;
    }

    // Validate JSON
    let configObj = null;
    if (formData.mappingConfig) {
      try {
        configObj = JSON.parse(formData.mappingConfig);
      } catch {
        showToast("La configurazione JSON non è valida", "error");
        return;
      }
    }

    // Validate regex patterns
    if (formData.emailPattern) {
      try {
        new RegExp(formData.emailPattern, "i");
      } catch {
        showToast("Pattern email non valido", "error");
        return;
      }
    }

    if (formData.subjectPattern) {
      try {
        new RegExp(formData.subjectPattern, "i");
      } catch {
        showToast("Pattern oggetto non valido", "error");
        return;
      }
    }

    setSaving(true);
    try {
      // Inject/remove pdf_router flag in config JSON
      if (formData.pdfRouter) {
        configObj = { ...configObj, pdf_router: true };
      } else if (configObj) {
        delete configObj.pdf_router;
      }

      const dataToSend = {
        ...formData,
        actionType: parseInt(formData.actionType),
        priorita: parseInt(formData.priorita) || 10,
        mappingConfig: configObj,
      };

      if (editingItem) {
        await apiRequest(`/email-mapping/${editingItem.id}`, {
          method: "PUT",
          body: JSON.stringify(dataToSend),
        });
        showToast("Mapping aggiornato con successo", "success");
      } else {
        await apiRequest("/email-mapping", {
          method: "POST",
          body: JSON.stringify(dataToSend),
        });
        showToast("Mapping creato con successo", "success");
      }

      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving mapping:", error);
      showToast(error.message || "Errore durante il salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testData.emailFrom || !testData.subject) {
      showToast("Email mittente e oggetto sono obbligatori", "error");
      return;
    }

    try {
      const result = await apiRequest(`/email-mapping/${editingItem.id}/test`, {
        method: "POST",
        body: JSON.stringify(testData),
      });
      setTestResults(result);
    } catch (error) {
      console.error("Error testing mapping:", error);
      showToast(error.message || "Errore durante il test", "error");
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await apiRequest(`/email-mapping/${itemToDelete.id}`, {
        method: "DELETE",
      });
      showToast("Mapping eliminato con successo", "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting mapping:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    }
  };

  const handleProcessEmails = async () => {
    setProcessing(true);
    try {
      const result = await apiRequest("/email-mapping/process-emails", {
        method: "POST",
        body: JSON.stringify({ maxEmails: 10 }),
      });
      showToast(
        `Elaborazione completata: ${result.success}/${result.processed} email processate`,
        result.errors > 0 ? "warning" : "success",
      );
    } catch (error) {
      console.error("Error processing emails:", error);
      showToast(error.message || "Errore durante elaborazione email", "error");
    } finally {
      setProcessing(false);
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  // Get fornitore name
  const getFornitoreNome = (id) => {
    const found = fornitori.find((f) => f.id === id);
    return found ? found.nome || found.phc_name : "-";
  };

  // Table columns
  const columns = [
    {
      key: "nome",
      label: "Nome",
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.nome}</div>
          <div className="text-xs text-gray-500">Priorità: {row.priorita}</div>
        </div>
      ),
    },
    {
      key: "fornitore",
      label: "Fornitore",
      sortable: true,
      render: (row) =>
        row.fornitoreNome || getFornitoreNome(row.fornitoreId) || "-",
    },
    {
      key: "patterns",
      label: "Pattern",
      render: (row) => (
        <div className="text-xs space-y-1">
          {row.emailPattern && (
            <div className="text-gray-600">
              <span className="text-gray-400">Email:</span>{" "}
              <code className="bg-gray-100 px-1 rounded">
                {row.emailPattern}
              </code>
            </div>
          )}
          {row.subjectPattern && (
            <div className="text-gray-600">
              <span className="text-gray-400">Oggetto:</span>{" "}
              <code className="bg-gray-100 px-1 rounded">
                {row.subjectPattern}
              </code>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "actionType",
      label: "Azione",
      render: (row) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          {getActionTypeLabel(row.actionType)}
        </span>
      ),
    },
    {
      key: "attivo",
      label: "Stato",
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.attivo
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {row.attivo ? "Attivo" : "Inattivo"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Azioni",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenTest(row);
            }}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="Testa"
          >
            🧪
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEdit(row);
            }}
            className="p-1 text-primary-600 hover:text-primary-800"
            title="Modifica"
          >
            ✏️
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              confirmDelete(row);
            }}
            className="p-1 text-red-600 hover:text-red-800"
            title="Elimina"
          >
            🗑️
          </button>
        </div>
      ),
    },
  ];

  // Prepare fornitori for select
  const fornitoriOptions = fornitori.map((f) => ({
    value: f.id,
    label: f.nome || f.phc_name,
  }));

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <a href="/admin" className="hover:text-primary-600">
              Admin
            </a>
            <span>/</span>
            <span>Email Mapping</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Email Mapping Fornitori
          </h1>
          <p className="text-gray-500 mt-1">
            Configura il parsing automatico delle email inviate dai fornitori
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleProcessEmails}
            disabled={processing}
          >
            {processing ? "Elaborazione..." : "▶ Elabora Email"}
          </Button>
          <Button variant="primary" onClick={handleOpenCreate}>
            + Nuovo Mapping
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <span className="text-blue-600 text-xl">📧</span>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              Come funziona il parsing automatico
            </p>
            <p className="text-blue-700">
              Quando un fornitore invia un'email a{" "}
              <code className="bg-blue-100 px-1 rounded">
                automation@peralbahc.com
              </code>
              , il sistema cerca un mapping corrispondente in base al mittente e
              all'oggetto. Se trova un match, estrae automaticamente dati (date,
              RSA, ecc.) e esegue le azioni configurate.
            </p>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-2xl font-bold text-gray-900">
            {mappings.length}
          </div>
          <div className="text-sm text-gray-500">Totale Mapping</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600">
            {mappings.filter((m) => m.attivo).length}
          </div>
          <div className="text-sm text-gray-500">Attivi</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-primary-600">
            {new Set(mappings.map((m) => m.fornitoreId).filter(Boolean)).size}
          </div>
          <div className="text-sm text-gray-500">Fornitori Configurati</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-purple-600">
            {mappings.filter((m) => m.actionType === 100000003).length}
          </div>
          <div className="text-sm text-gray-500">Mapping Completi</div>
        </Card>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <DataTable
          data={mappings}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca mapping..."
          emptyMessage="Nessun mapping configurato"
          onRowClick={handleOpenEdit}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? "Modifica Mapping" : "Nuovo Mapping"}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome *"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              placeholder="es. Rapportini Fornitore XYZ"
              maxLength={100}
            />

            <Select
              label="Fornitore"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleChange}
              options={fornitoriOptions}
              placeholder="Seleziona fornitore..."
            />
          </div>

          {/* Pattern Section */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <h3 className="text-sm font-medium text-gray-700">
              Pattern di Riconoscimento (Regex)
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Pattern Email Mittente"
                name="emailPattern"
                value={formData.emailPattern}
                onChange={handleChange}
                placeholder="es. @fornitore\.it$"
                helperText="Regex per identificare il mittente"
              />

              <Input
                label="Pattern Oggetto Email"
                name="subjectPattern"
                value={formData.subjectPattern}
                onChange={handleChange}
                placeholder="es. rapportino"
                helperText="Regex per identificare l'oggetto"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Pattern Data"
                name="datePattern"
                value={formData.datePattern}
                onChange={handleChange}
                placeholder="es. (\d{1,2}/\d{1,2}/\d{4})"
                helperText="Per estrarre date"
              />

              <Input
                label="Pattern RSA"
                name="rsaPattern"
                value={formData.rsaPattern}
                onChange={handleChange}
                placeholder="es. RSA\s+(\w+)"
                helperText="Per identificare RSA"
              />

              <Input
                label="Pattern Intervento"
                name="interventoPattern"
                value={formData.interventoPattern}
                onChange={handleChange}
                placeholder="es. intervento\s+(\d+)"
                helperText="Per identificare intervento"
              />
            </div>
          </div>

          {/* Actions Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Tipo Azione"
              name="actionType"
              value={formData.actionType}
              onChange={handleChange}
              options={ACTION_TYPES}
            />

            <Input
              label="Priorità"
              name="priorita"
              type="number"
              min="1"
              max="100"
              value={formData.priorita}
              onChange={handleChange}
              helperText="Più basso = priorità maggiore"
            />

            <div className="flex flex-col gap-3 items-start pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="attivo"
                  checked={formData.attivo}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                <span className="text-gray-700">Mapping attivo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="pdfRouter"
                  checked={formData.pdfRouter}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                <span className="text-gray-700">PDF Router</span>
              </label>
              {formData.pdfRouter && (
                <p className="text-xs text-blue-600 bg-blue-50 rounded px-2 py-1">
                  Il fornitore viene rilevato automaticamente dal contenuto del
                  PDF allegato. Utile per mittenti interni che inoltrano fatture
                  di fornitori diversi.
                </p>
              )}
            </div>
          </div>

          {/* JSON Config */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configurazione Avanzata (JSON)
            </label>
            <textarea
              name="mappingConfig"
              value={formData.mappingConfig}
              onChange={handleChange}
              className="form-input w-full h-64 font-mono text-xs"
              placeholder="{}"
            />
            <p className="text-xs text-gray-500 mt-1">
              Configurazione dettagliata per extractors, actions e notifications
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setModalOpen(false)}
            >
              Annulla
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Salvataggio..." : editingItem ? "Aggiorna" : "Crea"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Test Modal */}
      <Modal
        isOpen={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        title={`Testa Mapping: ${editingItem?.nome || ""}`}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 rounded-lg text-sm text-yellow-800">
            Inserisci i dati di una email di esempio per verificare se il
            mapping funziona correttamente.
          </div>

          <Input
            label="Email Mittente *"
            name="emailFrom"
            value={testData.emailFrom}
            onChange={handleTestChange}
            placeholder="es. rapporti@fornitore.it"
          />

          <Input
            label="Oggetto Email *"
            name="subject"
            value={testData.subject}
            onChange={handleTestChange}
            placeholder="es. Rapportino intervento RSA Mazzini del 15/01/2026"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Corpo Email (opzionale)
            </label>
            <textarea
              name="body"
              value={testData.body}
              onChange={handleTestChange}
              className="form-input w-full h-24"
              placeholder="Testo del corpo email..."
            />
          </div>

          <Button onClick={handleTest} variant="primary" className="w-full">
            Esegui Test
          </Button>

          {/* Test Results */}
          {testResults && (
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <h4 className="font-medium text-gray-900">Risultati Test</h4>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div
                  className={`p-3 rounded ${testResults.emailPatternMatch ? "bg-green-100" : "bg-red-100"}`}
                >
                  <span className="font-medium">Pattern Email:</span>{" "}
                  {testResults.emailPatternMatch ? "✅ Match" : "❌ No match"}
                </div>
                <div
                  className={`p-3 rounded ${testResults.subjectPatternMatch ? "bg-green-100" : "bg-red-100"}`}
                >
                  <span className="font-medium">Pattern Oggetto:</span>{" "}
                  {testResults.subjectPatternMatch ? "✅ Match" : "❌ No match"}
                </div>
              </div>

              <div
                className={`p-3 rounded text-center ${testResults.wouldMatch ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}`}
              >
                <span className="font-bold">
                  {testResults.wouldMatch
                    ? "✅ EMAIL VERREBBE ELABORATA"
                    : "❌ EMAIL NON VERREBBE ELABORATA"}
                </span>
              </div>

              {Object.keys(testResults.extractedData || {}).length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">
                    Dati Estratti:
                  </h5>
                  <div className="bg-white p-3 rounded border text-xs font-mono">
                    {Object.entries(testResults.extractedData).map(
                      ([key, val]) => (
                        <div
                          key={key}
                          className="flex justify-between py-1 border-b last:border-0"
                        >
                          <span className="text-gray-600">{key}:</span>
                          <span
                            className={
                              val.match ? "text-green-600" : "text-red-600"
                            }
                          >
                            {val.match || val.error || "null"}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button variant="ghost" onClick={() => setTestModalOpen(false)}>
              Chiudi
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Mapping"
        message={`Sei sicuro di voler eliminare il mapping "${itemToDelete?.nome}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default EmailMappingPage;
